import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import {
  useGetLocalDevicesQuery,
  useCreateLocalDeviceMutation,
  useUpdateLocalDeviceMutation,
  useDeleteLocalDeviceMutation,
} from "../../store/api";
import { useStoreFeatures } from "../../hooks/useStoreFeatures";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";

const DEVICE_TYPES = ["KIOSK", "PRINTER", "KDS", "DISPLAY", "POS"];

const LocalDevices = () => {
  const [selectedType, setSelectedType] = useState("");
  const user = useSelector((state) => state.auth.user);
  const {
    features,
    isLoading: featuresLoading,
    isError: featuresError,
    refetch: refetchFeatures,
  } = useStoreFeatures(true);
  const { data, isLoading, isError, error } =
    useGetLocalDevicesQuery(selectedType);
  const [createDevice, createState] = useCreateLocalDeviceMutation();
  const [updateDevice, updateState] = useUpdateLocalDeviceMutation();
  const [deleteDevice] = useDeleteLocalDeviceMutation();

  const [form, setForm] = useState({
    deviceName: "",
    deviceType: "",
    deviceKey: "",
    pairingCode: "",
    ipAddress: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    deviceId: null,
    deviceName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const devices = Array.isArray(data) ? data : [];

  const allowedTypes = useMemo(() => {
    const allowed = new Set();
    const canPos =
      Boolean(features?.enablePos) &&
      hasPermission(user, PERMISSIONS.POS_VIEW);
    const canKiosk =
      Boolean(features?.enableKiosk) &&
      hasPermission(user, PERMISSIONS.KIOSK_VIEW);
    const canKds =
      Boolean(features?.enableKds) &&
      hasPermission(user, PERMISSIONS.KDS_VIEW);
    const canOrderDisplay =
      Boolean(features?.enableOrderDisplay) &&
      hasPermission(user, PERMISSIONS.ORDER_DISPLAY_VIEW);

    if (canPos) {
      allowed.add("POS");
      allowed.add("PRINTER");
    }
    if (canKiosk) allowed.add("KIOSK");
    if (canKds) allowed.add("KDS");
    if (canOrderDisplay) allowed.add("DISPLAY");
    return DEVICE_TYPES.filter((type) => allowed.has(type));
  }, [features, user]);

  const visibleDevices = devices.filter((device) =>
    allowedTypes.includes(device.deviceType),
  );

  const maxKds =
    features && features.maxKds !== undefined ? features.maxKds : null;
  const maxOrderDisplays =
    features && features.maxOrderDisplays !== undefined
      ? features.maxOrderDisplays
      : null;
  const kdsCount = visibleDevices.filter((d) => d.deviceType === "KDS").length;
  const displayCount = visibleDevices.filter(
    (d) => d.deviceType === "DISPLAY",
  ).length;
  const kdsLimitReached =
    maxKds !== null && Number.isFinite(maxKds) ? kdsCount >= maxKds : false;
  const orderDisplayLimitReached =
    maxOrderDisplays !== null && Number.isFinite(maxOrderDisplays)
      ? displayCount >= maxOrderDisplays
      : false;
  const addBlockedByLimit =
    (form.deviceType === "KDS" && kdsLimitReached) ||
    (form.deviceType === "DISPLAY" && orderDisplayLimitReached);

  useEffect(() => {
    if (selectedType && !allowedTypes.includes(selectedType)) {
      setSelectedType("");
    }
  }, [selectedType, allowedTypes]);

  if (featuresLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Local Devices" subtitle="Loading store features..." />
        <Card>
          <Alert tone="info">Loading store features...</Alert>
        </Card>
      </div>
    );
  }

  if (featuresError || !features) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Local Devices"
          subtitle="Unable to load store features."
        />
        <Card className="space-y-3">
          <Alert tone="error">Failed to load store features.</Alert>
          <Button size="sm" onClick={() => refetchFeatures && refetchFeatures()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const validateIPAddress = (ip) => {
    if (!ip) return true; // Optional field
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(ip)) return false;
    const parts = ip.split(".");
    return parts.every((part) => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate IP address if provided
    if (form.ipAddress && !validateIPAddress(form.ipAddress)) {
      setErrorModal({
        isOpen: true,
        message: "Please enter a valid IP address (e.g., 192.168.1.100)",
      });
      return;
    }

    try {
      const payload = {
        deviceName: form.deviceName,
        deviceType: form.deviceType,
        deviceKey: form.deviceKey,
        pairingCode: form.pairingCode,
        ipAddress: form.ipAddress || null,
        isActive: form.isActive,
      };

      console.log("Sending payload:", payload);

      if (editingId) {
        await updateDevice({
          deviceId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createDevice(payload).unwrap();
      }
      setForm({
        deviceName: "",
        deviceType: "",
        deviceKey: "",
        pairingCode: "",
        ipAddress: "",
        isActive: true,
      });
      setShowForm(false);
    } catch (err) {
      console.error("Error creating/updating device:", err);
      setErrorModal({
        isOpen: true,
        message:
          err?.data?.detail ||
          err?.data?.message ||
          err?.message ||
          "Failed to save device. Please check the console for details.",
      });
    }
  };

  const handleEdit = (device) => {
    setEditingId(device.deviceId);
    setForm({
      deviceName: device.deviceName || "",
      deviceType: device.deviceType || "",
      deviceKey: device.deviceKey || "",
      pairingCode: device.pairingCode || "",
      ipAddress: device.ipAddress || "",
      isActive: device.isActive !== undefined ? device.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (deviceId) => {
    const device = devices.find((d) => d.deviceId === deviceId);
    setDeleteModal({
      isOpen: true,
      deviceId,
      deviceName: device?.deviceName || "this device",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteDevice(deleteModal.deviceId).unwrap();
      setDeleteModal({ isOpen: false, deviceId: null, deviceName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, deviceId: null, deviceName: "" });
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to delete device.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      deviceName: "",
      deviceType: "",
      deviceKey: "",
      pairingCode: "",
      ipAddress: "",
      isActive: true,
    });
    setShowForm(false);
  };

  const columns = [
    { key: "deviceName", label: "Device Name" },
    { key: "deviceType", label: "Type" },
    { key: "pairingCode", label: "Pairing Code" },
    { key: "ipAddress", label: "IP Address" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = visibleDevices.map((device) => {
    return {
      id: device.deviceId,
      deviceName: device.deviceName,
      deviceType: device.deviceType,
      pairingCode: device.pairingCode || "—",
      ipAddress: device.ipAddress || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {device.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex gap-2">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(device)}
            title="Edit"
            aria-label="Edit device"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            type="button"
            className="p-1 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded transition"
            onClick={() => handleDelete(device.deviceId)}
            title="Delete"
            aria-label="Delete device"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Local Devices"
        subtitle={
          allowedTypes.length > 0
            ? `Manage devices (${allowedTypes.join(", ")}).`
            : "No device types available for this user."
        }
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              disabled={allowedTypes.length === 0}
            >
              Add Device
            </Button>
          )
        }
      />
      {addBlockedByLimit && (
        <Alert tone="warning">
          Device limit reached for {form.deviceType}.
        </Alert>
      )}

      {!showForm && (
        <Card className="p-0">
          {/* Filter Section */}
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Select
                  label="Filter by Device Type"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="">All Devices</option>
                  {allowedTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Showing {visibleDevices.length} device
                {visibleDevices.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading devices...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load devices."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No devices yet."
              />
            )}
          </div>
        </Card>
      )}

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Device" : "Add Device"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Device Name"
                name="deviceName"
                placeholder="Kiosk 01"
                value={form.deviceName}
                onChange={handleChange}
                required
              />
              <Select
                label="Device Type"
                name="deviceType"
                value={form.deviceType}
                onChange={handleChange}
                required
              >
                <option value="">Select type</option>
                {allowedTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Device Key"
                name="deviceKey"
                placeholder="KIOSK-01"
                value={form.deviceKey}
                onChange={handleChange}
                required
              />
              <Input
                label="Pairing Code"
                name="pairingCode"
                placeholder="1111-1111"
                value={form.pairingCode}
                onChange={handleChange}
                required
              />
            </div>

            <Input
              label="IP Address (Optional)"
              name="ipAddress"
              placeholder="192.168.1.100"
              value={form.ipAddress}
              onChange={handleChange}
              helper="Enter a valid IPv4 address (e.g., 192.168.1.100)"
            />

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Device {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save device."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={
                  createState.isLoading ||
                  updateState.isLoading ||
                  (!editingId && addBlockedByLimit)
                }
              >
                {editingId ? "Update" : "Add"} Device
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, deviceId: null, deviceName: "" })
        }
        title="Delete Device"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.deviceName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default LocalDevices;
